import { computed } from 'vue';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { storeToRefs } from 'pinia';
import promotionsConfig from '@/features/promotions/config.json';
import { MaybeRefOrGetter, toValue } from '@vueuse/core';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { ClickoutUrl } from '@/helpers/clickout-helper';
import { getPrimeAssociatesSuffix } from '@/helpers/promotion-helper';

export function useNativeAds(props: {
	title: MaybeRefOrGetter<TitleDetail | undefined>;
	slot: 'sqtv-list' | 'backdrop';
}) {
	const { country } = storeToRefs(useLanguageStore());
	const { isProUser } = storeToRefs(useUserStore());
	const title = computed(() => toValue(props.title));

	const appleProgrammaticCountries = computed(() => {
		return promotionsConfig.gam?.apple_programmatic ?? ([] as string[]);
	});

	const shouldShowNativeAd = computed(() => {
		if (isProUser.value || !appleProgrammaticCountries.value.includes(country.value)) return false;

		if (
			props.slot === 'sqtv-list' &&
			title.value &&
			title.value.offers.some(offer => offer.package.shortName === 'atp')
		)
			return false;

		return true;
	});

	/* Set the tag for the SQTV fallback, that is rendered underneath Native SQTV in case of GAM failure */
	function setSQTVFallbackTag(clickoutUrl: ClickoutUrl, shortName: string) {
		if (!shouldShowNativeAd.value) return clickoutUrl;
		let tag = 'native-sqtv-fb';

		if (['amp', 'prv'].includes(shortName)) {
			const primeSuffix = getPrimeAssociatesSuffix(country.value);
			tag = primeSuffix ? `${tag}-${primeSuffix}` : tag;
		}

		return clickoutUrl.set('uct_ctag', tag);
	}

	return {
		shouldShowNativeAd,
		appleProgrammaticCountries,
		setSQTVFallbackTag,
	};
}
